import { useState } from "react";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { NoteDisplay } from "shared/app/Notes/components/NoteDisplay";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import type { ApplicationNote, NoteAttachment } from "shared/app/Notes/types";
import { DateTime } from "luxon";
import { Card } from "shared/components/Card";
import { cx } from "shared/utils";
import { isUnread } from "shared/app/Notes/helpers";
import { useAuthContext } from "providers/Auth";
import { AssetViewer } from "shared/app/AssetViewer";

export const LastNoteCell = ({
  note,
  onViewNotes,
  applicationId,
  openApplicationNotes
}: {
  note?: ApplicationNote | null;
  onViewNotes?: () => void;
  applicationId?: number;
  openApplicationNotes?: (id: number) => void;
}) => {
  const { auth } = useAuthContext();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] =
    useState<NoteAttachment | null>(null);

  const handlePopoverOpenChange = (open: boolean) => {
    if (!open && selectedAttachment) return;
    setIsPopoverOpen(open);
  };

  const handleViewNotes =
    onViewNotes ??
    (applicationId != null && openApplicationNotes
      ? () => openApplicationNotes(applicationId)
      : undefined);

  if (!note) return <span className="text-xs text-content-300">-</span>;

  const noteContent =
    note.rich_text?.document_html ??
    note.rich_text?.document_text ??
    String(note.message);

  const isUnreadNote = auth?.id != null && isUnread(note, auth.id);

  return (
    <>
      <Popover
        placement="bottom-end"
        trigger="hover"
        hoverDelay={{ open: 200, close: 0 }}
        open={isPopoverOpen || !!selectedAttachment}
        onOpenChange={handlePopoverOpenChange}
      >
        <PopoverTrigger asChild>
          <div
            className="group w-full flex flex-col gap-2 justify-center !cursor-pointer px-2 py-[18px]"
            onClick={handleViewNotes}
          >
            <div className="relative w-full min-w-0">
              <Show if={isUnreadNote}>
                <span
                  className="pointer-events-none absolute left-0 top-[0.35rem] size-2 shrink-0 rounded-full bg-primary-500 ring-2 ring-alpha-40"
                  aria-hidden
                />
              </Show>
              <NoteDisplay
                content={noteContent}
                containerClassName={cx(
                  // layout
                  "flex-grow",
                  // cursor
                  "!cursor-pointer [&_*]:!cursor-pointer [&_.tiptap_a]:!cursor-pointer [&_.tiptap_a]:!pointer-events-none",
                  // truncate single line
                  "[&_.tiptap]:overflow-hidden [&_.tiptap]:text-ellipsis",
                  "[&_.tiptap]:[white-space:nowrap!important] [&_.tiptap_*]:[white-space:nowrap!important]",
                  // inline block elements
                  "[&_.tiptap_p]:inline [&_.tiptap_p]:after:content-['_']",
                  "[&_.tiptap_br]:hidden [&_.tiptap_br+*]:before:content-['_']",
                  "[&_.tiptap_li]:inline [&_.tiptap_li]:after:content-['_']",
                  "[&_.tiptap_ul]:inline [&_.tiptap_ol]:inline",
                  // strip formatting → plain text
                  "!text-content-1000 [&_.tiptap_*]:!font-normal [&_.tiptap_*]:!not-italic [&_.tiptap_*]:!no-underline",
                  "[&_.tiptap_a]:!text-content-1000",
                  "[&_.tiptap_ul]:!list-none [&_.tiptap_ol]:!list-none [&_.tiptap_ul]:!pl-0 [&_.tiptap_ol]:!pl-0 [&_.tiptap_li]:!pl-0 [&_.tiptap_li::marker]:!hidden",
                  // mentions stay bold
                  "[&_.tiptap_.mention-component]:!font-bold [&_.tiptap_.mention-component_*]:!font-bold",
                  "group-hover:[&_.tiptap_*:not(.mention-component):not(.mention-component_*)]:!font-bold",
                  isUnreadNote && "pl-4"
                )}
              />
            </div>
            <div className="flex justify-between text-content-400 text-[13px] leading-4">
              <Show if={note.author?.name}>
                <p className="truncate">{note.author?.name}</p>
              </Show>
              <p>
                {note.created_at
                  ? DateTime.fromISO(note.created_at).toFormat(
                      "MMM dd 'at' h:mm a"
                    )
                  : ""}
              </p>
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent className="max-w-[531px] w-full z-[50] shadow-lg">
          <Card className="px-6 py-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-base font-bold text-content-1000">
                {note.author?.name}{" "}
                <span className="font-normal">left a note</span>
              </p>
              {handleViewNotes && (
                <Button
                  className="!text-content-500"
                  variant={ButtonVariant.TEXT}
                  size={ButtonSize.SM}
                  onClick={handleViewNotes}
                >
                  View notes
                </Button>
              )}
            </div>
            <NoteDisplay
              content={noteContent}
              attachments={note.attachments}
              onAttachmentClick={setSelectedAttachment}
            />
            <div className="border-t border-alpha-40 mt-4 pb-4" />
            <p className="text-content-200 text-[13px]">
              {note.created_at
                ? DateTime.fromISO(note.created_at).toFormat(
                    "MMM dd, yyyy 'at' h:mm a"
                  )
                : ""}
            </p>
          </Card>
        </PopoverContent>
      </Popover>
      {selectedAttachment && (
        <AssetViewer
          src={selectedAttachment.url}
          filename={selectedAttachment.original_filename}
          open
          onClose={() => setSelectedAttachment(null)}
          mimeType={selectedAttachment.content_type}
          fileSize={selectedAttachment.size}
        />
      )}
    </>
  );
};
